/**
 * Activity Monitor Service
 *
 * Detects user activity and sends heartbeat to backend to keep session alive.
 * Part of Phase 63 Session Management Redesign.
 *
 * Key Features:
 * - Listens for: mousemove, keydown, scroll, click, touchstart
 * - Sends heartbeat every 60s when user is active
 * - Pauses when tab is hidden
 * - Cross-tab coordination via localStorage
 * - Redirects to login on 401 (session expired)
 */

import { apiClient } from './api'
import { sessionValidationService } from './sessionValidationService'

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'] as const
const HEARTBEAT_INTERVAL_MS = 60_000 // 60 seconds
const THROTTLE_MS = 1000 // Don't spam on every mousemove
const HEARTBEAT_TIMEOUT_MS = 45_000 // Abort if heartbeat takes too long
const STORAGE_KEY = 'session_expires_at'
const HEARTBEAT_OWNER_KEY = 'heartbeat_owner'
const HEARTBEAT_OWNER_TTL_MS = 70_000 // Slightly longer than heartbeat interval

type HeartbeatCallback = (expiresAt: string) => void

/**
 * Throttle function - limits how often a function can be called
 */
function throttle<T extends (...args: unknown[]) => void>(fn: T, limit: number): T {
  let lastCall = 0
  return ((...args: unknown[]) => {
    const now = Date.now()
    if (now - lastCall >= limit) {
      lastCall = now
      fn(...args)
    }
  }) as T
}

class ActivityMonitor {
  private lastActivity: number = Date.now()
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private isActive: boolean = false
  private isRunning: boolean = false
  private abortController: AbortController | null = null
  private pendingHeartbeat: boolean = false
  private tabId: string = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  private onExpiryUpdate: HeartbeatCallback | null = null

  /**
   * Start monitoring user activity.
   * Call this after successful login.
   */
  start(onExpiryUpdate?: HeartbeatCallback): void {
    if (this.isRunning) {
      console.log('[ActivityMonitor] Already running, skipping start')
      return
    }

    console.log('[ActivityMonitor] Starting activity monitoring')
    this.isRunning = true
    this.onExpiryUpdate = onExpiryUpdate || null

    // Listen for user activity (throttled)
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, this.onActivity, { passive: true })
    })

    // Listen for visibility changes (pause when tab hidden)
    document.addEventListener('visibilitychange', this.onVisibilityChange)

    // Listen for cross-tab session updates
    window.addEventListener('storage', this.onStorageChange)

    // Start heartbeat loop
    this.heartbeatTimer = setInterval(
      this.sendHeartbeatIfActive,
      HEARTBEAT_INTERVAL_MS
    )

    // Try to become heartbeat owner
    this.tryBecomeHeartbeatOwner()
  }

  /**
   * Stop monitoring user activity.
   * Call this before logout.
   */
  stop(): void {
    if (!this.isRunning) {
      console.log('[ActivityMonitor] Not running, skipping stop')
      return
    }

    console.log('[ActivityMonitor] Stopping activity monitoring')
    this.isRunning = false
    this.onExpiryUpdate = null

    // Abort any pending heartbeat
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }

    // Remove all event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, this.onActivity)
    })
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    window.removeEventListener('storage', this.onStorageChange)

    // Clear heartbeat timer
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }

    // Relinquish heartbeat ownership
    this.relinquishHeartbeatOwnership()
  }

  /**
   * Check if monitor is currently running
   */
  get running(): boolean {
    return this.isRunning
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE: Activity Detection
  // ═══════════════════════════════════════════════════════════════════════════

  private onActivity = throttle(() => {
    if (document.hidden) return // Don't track activity when tab is hidden
    this.lastActivity = Date.now()
    this.isActive = true
  }, THROTTLE_MS)

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      // Tab became hidden - pause activity tracking
      this.isActive = false
    }
    // When tab becomes visible again, next user action will set isActive = true
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE: Cross-Tab Coordination
  // ═══════════════════════════════════════════════════════════════════════════

  private onStorageChange = (event: StorageEvent): void => {
    // Cross-tab coordination: if another tab extended session, update local state
    if (event.key === STORAGE_KEY && event.newValue) {
      console.log('[ActivityMonitor] Session expiry updated by another tab:', event.newValue)
      if (this.onExpiryUpdate) {
        this.onExpiryUpdate(event.newValue)
      }
    }
  }

  private tryBecomeHeartbeatOwner(): boolean {
    const now = Date.now()
    const ownerData = localStorage.getItem(HEARTBEAT_OWNER_KEY)

    if (ownerData) {
      try {
        const { tabId, timestamp } = JSON.parse(ownerData)
        // If current owner is still active (within TTL), don't take over
        if (now - timestamp < HEARTBEAT_OWNER_TTL_MS && tabId !== this.tabId) {
          return false
        }
      } catch {
        // Invalid data, take ownership
      }
    }

    // Take ownership
    localStorage.setItem(HEARTBEAT_OWNER_KEY, JSON.stringify({
      tabId: this.tabId,
      timestamp: now,
    }))

    try {
      const updatedOwnerData = localStorage.getItem(HEARTBEAT_OWNER_KEY)
      if (!updatedOwnerData) {
        return false
      }

      const { tabId } = JSON.parse(updatedOwnerData)
      return tabId === this.tabId
    } catch {
      return false
    }
  }

  private isHeartbeatOwner(): boolean {
    const ownerData = localStorage.getItem(HEARTBEAT_OWNER_KEY)
    if (!ownerData) {
      // No owner, become owner
      return this.tryBecomeHeartbeatOwner()
    }

    try {
      const { tabId, timestamp } = JSON.parse(ownerData)
      const now = Date.now()

      // If owner timed out, take over
      if (now - timestamp >= HEARTBEAT_OWNER_TTL_MS) {
        return this.tryBecomeHeartbeatOwner()
      }

      return tabId === this.tabId
    } catch {
      return this.tryBecomeHeartbeatOwner()
    }
  }

  private refreshOwnership(): void {
    if (this.isHeartbeatOwner()) {
      localStorage.setItem(HEARTBEAT_OWNER_KEY, JSON.stringify({
        tabId: this.tabId,
        timestamp: Date.now(),
      }))
    }
  }

  private relinquishHeartbeatOwnership(): void {
    const ownerData = localStorage.getItem(HEARTBEAT_OWNER_KEY)
    if (ownerData) {
      try {
        const { tabId } = JSON.parse(ownerData)
        if (tabId === this.tabId) {
          localStorage.removeItem(HEARTBEAT_OWNER_KEY)
        }
      } catch {
        // Ignore
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE: Heartbeat
  // ═══════════════════════════════════════════════════════════════════════════

  private sendHeartbeatIfActive = async (): Promise<void> => {
    // Skip if no activity since last heartbeat
    if (!this.isActive) {
      return
    }

    // Skip if previous heartbeat still in flight
    if (this.pendingHeartbeat) {
      console.log('[ActivityMonitor] Previous heartbeat still pending, skipping')
      return
    }

    // Skip if tab is hidden
    if (document.hidden) {
      return
    }

    // Skip if not heartbeat owner (another tab will send)
    if (!this.isHeartbeatOwner()) {
      console.log('[ActivityMonitor] Not heartbeat owner, skipping')
      return
    }

    this.isActive = false // Reset for next interval
    this.pendingHeartbeat = true
    this.abortController = new AbortController()

    // Timeout: abort if heartbeat takes too long
    const timeoutId = setTimeout(() => {
      if (this.abortController) {
        this.abortController.abort()
      }
    }, HEARTBEAT_TIMEOUT_MS)

    try {
      console.log('[ActivityMonitor] Sending heartbeat')

      const response = await apiClient.post('/api/session/heartbeat', null, {
        signal: this.abortController.signal,
      })

      const expiresAt = response.data?.expiresAt
      if (expiresAt) {
        console.log('[ActivityMonitor] Heartbeat success, expires at:', expiresAt)

        // Update local callback
        if (this.onExpiryUpdate) {
          this.onExpiryUpdate(expiresAt)
        }

        // Broadcast to other tabs via localStorage
        localStorage.setItem(STORAGE_KEY, expiresAt)

        // Refresh ownership timestamp
        this.refreshOwnership()
      }
    } catch (error: unknown) {
      // Handle abort
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn('[ActivityMonitor] Heartbeat timed out, will retry next interval')
        return
      }

      // Handle 401 - session expired
      // Phase 63: Redirect to login on session expiration
      const axiosError = error as { response?: { status?: number } }
      if (axiosError.response?.status === 401) {
        console.log('[ActivityMonitor] Session expired (401), stopping and redirecting to login')
        this.stop()
        // Redirect to login with route preservation
        sessionValidationService.handleSessionExpired()
        return
      }

      // Handle rate limit
      if (axiosError.response?.status === 429) {
        console.warn('[ActivityMonitor] Rate limited, will retry next interval')
        return
      }

      // Network error - log and retry next interval
      console.warn('[ActivityMonitor] Heartbeat failed, will retry:', error)
    } finally {
      clearTimeout(timeoutId)
      this.pendingHeartbeat = false
      this.abortController = null
    }
  }
}

// Singleton instance
export const activityMonitor = new ActivityMonitor()
export default activityMonitor
